import BaseModal from "../../../../components/ui/BaseModal";
import BaseTextField from "../../../../components/ui/BaseTextField";
import BaseButton from "../../../../components/ui/BaseButton";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createCompanyUser,
  updateCompanyUser,
} from "../../../../services/api/company";
import { useDispatch } from "react-redux";
import { showToast } from "../../../../store/toast/toastSlice";
import SearchLocation from "../../../../components/SearchLocation/SearchLocation";
import BaseAutoSelect from "../../../../components/ui/BaseAutoSelect";
import { fetchAllCompanyDesignations } from "../../../../services/api/designation";
const getValidationSchema = (isEdit) =>
  yup.object({
    firstName: yup.string("").required("Enter First name"),
    lastName: yup.string("Enter Last name").required("Enter Last name"),
    designation: yup.number("Enter Designation").required("Enter designation"),
    password: isEdit
      ? yup.string().notRequired()
      : yup
          .string("Enter a valid password")
          .required("Password is required")
          .min(8, "Password must be at least 8 characters")
          .matches(/[a-zA-Z]/, "Password must contain at least one letter")
          .matches(/\d/, "Password must contain at least one number")
          .matches(
            /[A-Z]/,
            "Password must contain at least one uppercase letter"
          )
          .matches(
            /[\W_]/,
            "Password must contain at least one special character"
          ),
    email: isEdit
      ? yup.string().notRequired()
      : yup.string().email("Enter email").required("Enter email"),
    phoneNumber: yup
      .string("Enter phone number")
      .required("Enter phone number"),
  });

const AddEditCompanyUserModal = ({
  open,
  close,
  setCompanyUsersList,
  selectedUser,
  setSelectedUser,
}) => {
  const dispatch = useDispatch();
  const {
    isPending: isNewCreatedUserApiLoading,
    error: isNewCreatedUserApiError,
    data: newCreatedUser,
    mutateAsync: createCompanyUserMutation,
  } = useMutation({
    mutationFn: createCompanyUser,
    enabled: false,
  });

  const {
    isPending: isUpdateCompanyUserApiLoading,
    error: isUpdateCompanyUserApiError,
    data: updatedUser,
    mutateAsync: updateCompanyUserMutation,
  } = useMutation({
    mutationFn: updateCompanyUser,
    enabled: false,
  });

  const {
    data: desginationsData,
    isPending: isFetchAllCompanyDesignationsApiLoading,
    error: isFetchAllCompanyDesignationsApiError,
    refetch: refetchAllCompanyDesignations,
  } = useQuery({
    queryKey: ["companyDesginations"],
    queryFn: () => fetchAllCompanyDesignations(),
  });

  const formik = useFormik({
    initialValues: {
      firstName: selectedUser?.firstName,
      lastName: selectedUser?.lastName,
      designation: selectedUser?.assignedDesignation?.id || "",
      password: "",
      email: selectedUser?.email,
      phoneNumber: selectedUser?.phoneNumber,
      country: selectedUser?.personalAddress?.country || "",
      addressLine1: selectedUser?.personalAddress?.addressLine1 || "",
      city: selectedUser?.personalAddress?.city || "",
      state: selectedUser?.personalAddress?.state || "",
      zipCode: selectedUser?.personalAddress?.zipCode || "",
      longitude: selectedUser?.personalAddress?.longitude || "",
      latitude: selectedUser?.personalAddress?.latitude || "",
      timeZone: selectedUser?.personalAddress?.timeZone || "",
    },
    validationSchema: getValidationSchema(!!selectedUser),
    onSubmit: (values) => {
      handleSubmitButton();
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const formattedDesignationsOptions = desginationsData?.data?.map(
    (designation) => {
      return {
        label: designation?.designation,
        value: designation?.id,
      };
    }
  );

  const handleSubmitButton = () => {
    if (selectedUser) {
      editCompanyUser();
    } else {
      createNewCompanyUser();
    }
  };

  const createNewCompanyUser = async () => {
    try {
      const newUser = await createCompanyUserMutation(formik.values);
      setCompanyUsersList((prevUsers) => [newUser?.data, ...prevUsers]);
      dispatch(
        showToast({
          message: "User created successfully",
          type: "success",
        })
      );
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "success",
        })
      );
    }
  };

  const editCompanyUser = async () => {
    try {
      const { email, password, ...rest } = formik.values;
      const payload = {
        id: selectedUser?.id,
        ...rest,
      };
      const newUser = await updateCompanyUserMutation(payload);
      setCompanyUsersList((prevUsers) =>
        prevUsers.map((user) =>
          user.id === newUser?.data?.id ? { ...user, ...newUser?.data } : user
        )
      );

      dispatch(
        showToast({
          message: "User updated successfully",
          type: "success",
        })
      );
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "success",
        })
      );
    }
  };

  console.log(formik.values);

  const handlePlaceSelected = (selectedAddress) => {
    formik.setValues({
      ...formik.values,
      country: selectedAddress.country,
      addressLine1: selectedAddress.completeAddress,
      city: selectedAddress.city,
      state: selectedAddress.state,
      zipCode: selectedAddress.zipCode,
      longitude: selectedAddress.longitude,
      latitude: selectedAddress.latitude,
      timeZone: selectedAddress.timeZone,
    });
  };

  return (
    <form>
      <BaseModal
        open={open}
        close={() => {
          close(false);
          setSelectedUser(null);
        }}
        className="-top-[200px] overflow-auto"
        style={{ width: "1000px" }}
      >
        <div className="px-8 py-5">
          <h6 className="text-dark font-medium border-b pb-1">
            {selectedUser ? "Update User" : "Invite New User"}
          </h6>
        </div>
        <div className="px-8 py-4 grid grid-cols-3 gap-x-6 gap-y-4">
          <BaseTextField
            label="First Name"
            labelShrink={false}
            optional={false}
            id="firstName"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />
          <BaseTextField
            label="Last Name"
            labelShrink={false}
            optional={false}
            id="lastName"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />
          <BaseTextField
            label="Phone Number"
            labelShrink={false}
            optional={false}
            id="phoneNumber"
            name="phoneNumber"
            value={formik.values.phoneNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
            }
            helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
          />
          <BaseTextField
            disabled={!!selectedUser}
            type="email"
            label="Email"
            labelShrink={false}
            optional={false}
            id="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          {!selectedUser && (
            <BaseTextField
              type="password"
              label="Password"
              labelShrink={false}
              optional={false}
              id="password"
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
          )}
          <SearchLocation
            onPlaceSelected={handlePlaceSelected}
            isDefaultField={true}
          />
          <div className="md:col-span-1 col-span-2">
            <BaseTextField
              disabled
              labelShrink={false}
              label="Address"
              optional={false}
              isDefaultField={false}
              required
              fullWidth
              id="addressLine1"
              name="addressLine1"
              value={formik.values.addressLine1}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.addressLine1 &&
                Boolean(formik.errors.addressLine1)
              }
              helperText={
                formik.touched.addressLine1 && formik.errors.addressLine1
              }
            />
          </div>
          <BaseTextField
            fullWidth
            fullHeight={false}
            label="Country"
            labelShrink={false}
            optional={false}
            id="country"
            name="country"
            value={formik.values.country || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.country && Boolean(formik.errors.country)}
            helperText={formik.touched.country && formik.errors.country}
          />
          <BaseAutoSelect
            required={true}
            label="Designation"
            labelShrink={false}
            name="designation"
            placeholder="Select Designation"
            options={formattedDesignationsOptions}
            value={formik.values.designation}
            onChange={(option) =>
              formik.setFieldValue("designation", option.target.value)
            }
            onBlur={formik.handleBlur}
            error={
              formik.touched.designation && Boolean(formik.errors.designation)
            }
            helperText={formik.touched.designation && formik.errors.designation}
            isClearable
          />
        </div>

        <div
          className="d-flex content-center  bg-white py-6 mt-6"
          style={{
            boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
          }}
        >
          <BaseButton
            className=" px-[28px] py-[5px] text-primary hover:!text-white"
            variant="outlined"
            color="primary"
            style={{
              marginRight: "16px",
            }}
            onClick={() => {
              close(false);
              setSelectedUser(null);
            }}
          >
            Cancel
          </BaseButton>

          <BaseButton
            disabled={Object.keys(formik.errors).length > 0}
            loaderSize="22px"
            isLoading={
              isNewCreatedUserApiLoading || isUpdateCompanyUserApiLoading
            }
            color="primary"
            className="py-[5px] px-[20px] min-w-32"
            type="submit"
            onClick={formik.handleSubmit}
          >
            {selectedUser ? "Update" : "Create"}
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
};

export default AddEditCompanyUserModal;
